export type MediaStats = {
  frameRate?: number;
  resolution?: '1080p' | '720p' | '480p' | '360p' | '240p' | '180p' | '144p';
  audioBitrate?: number;
  videoBitrate?: number;
  bitrate?: number;
};

export const getMediaStats = (
  report: RTCStatsReport,
  direction: 'inbound' | 'outbound',
  previousReport?: RTCStatsReport,
): MediaStats => {
  const mediaStats: MediaStats = {};

  for (const stat of report.values()) {
    const previousStat = previousReport?.get(stat.id);

    if (isVideoStat(stat, direction)) {
      const { height } = getDimensions(stat);

      mediaStats.frameRate = stat.framesPerSecond;
      mediaStats.resolution = getResolution(height);
      mediaStats.videoBitrate = getBitrate(stat, direction, previousStat);
    }

    if (isAudioStat(stat, direction)) {
      mediaStats.audioBitrate = getBitrate(stat, direction, previousStat);
    }
  }

  mediaStats.bitrate =
    mediaStats.videoBitrate || mediaStats.audioBitrate
      ? (mediaStats.videoBitrate || 0) + (mediaStats.audioBitrate || 0)
      : undefined;

  return mediaStats;
};

/**
 * Calculate the bitrate in bits per second
 *
 * @param stat - The current stats object
 * @param direction - The direction of the stats
 * @param previousStat - The previous stats object
 * @returns The bitrate in bits per second
 */
const getBitrate = (
  stat: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
  direction: 'inbound' | 'outbound',
  previousStat?: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
): number | undefined => {
  const bytes =
    direction === 'outbound'
      ? (stat as RTCOutboundRtpStreamStats).bytesSent
      : (stat as RTCInboundRtpStreamStats).bytesReceived;

  if (!previousStat || !bytes) {
    return bytes || undefined;
  }

  const previousBytes =
    direction === 'outbound'
      ? (previousStat as RTCOutboundRtpStreamStats).bytesSent
      : (previousStat as RTCInboundRtpStreamStats).bytesReceived;

  /**
   * bytes - previousBytes → difference in sent/received bytes
   * stat.timestamp - previousStat.timestamp → difference in ms
   * 1000 → convert ms to seconds
   * 8 → convert bytes to bits
   */
  return Math.round(
    ((bytes - (previousBytes || 0)) /
      (stat.timestamp - previousStat.timestamp)) *
      1000 *
      8,
  );
};

const getDimensions = (
  stat: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
): { width?: number; height?: number } => {
  return stat.frameWidth && stat.frameHeight
    ? stat.frameWidth > stat.frameHeight
      ? { width: stat.frameWidth, height: stat.frameHeight }
      : { width: stat.frameHeight, height: stat.frameWidth }
    : {};
};

const getResolution = (
  height: number | undefined,
): MediaStats['resolution'] => {
  if (!height) {
    return undefined;
  }

  return height >= 1080
    ? '1080p'
    : height >= 720
      ? '720p'
      : height >= 480
        ? '480p'
        : height >= 360
          ? '360p'
          : height >= 240
            ? '240p'
            : height >= 180
              ? '180p'
              : '144p';
};

const isVideoStat = <T extends 'inbound' | 'outbound'>(
  stat: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
  direction: T,
): stat is T extends 'inbound'
  ? RTCInboundRtpStreamStats
  : RTCOutboundRtpStreamStats => {
  return stat.type.startsWith(direction) && stat.kind === 'video';
};

const isAudioStat = <T extends 'inbound' | 'outbound'>(
  stat: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats,
  direction: T,
): stat is T extends 'inbound'
  ? RTCInboundRtpStreamStats
  : RTCOutboundRtpStreamStats => {
  return stat.type.startsWith(direction) && stat.kind === 'audio';
};
